import { motion, useReducedMotion } from "framer-motion";
import { Package as PackageIcon, ClipboardCheck } from "lucide-react";

/**
 * Small looped animation showing how a package works:
 * a pack with 10 credits, a session is logged, credits tick down 10 → 9 → 8.
 * Pure SVG/divs, themed via design tokens, reduced-motion safe.
 */
export function CreditFlowAnimation({ className = "" }: { className?: string }) {
  const reduce = useReducedMotion();
  const credits = reduce ? [8] : [10, 9, 8];
  const duration = 3; // seconds per full loop

  return (
    <div
      className={`relative w-full max-w-md mx-auto rounded-xl border bg-card p-5 ${className}`}
      aria-hidden="true"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <PackageIcon className="h-5 w-5 text-brand-text" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">10-lesson pack</div>
          <div className="text-xs text-muted-foreground">Sarah J.</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tabular-nums text-brand-text relative h-8 w-10 overflow-hidden">
            {credits.map((n, i) => (
              <motion.span
                key={n + "-" + i}
                className="absolute inset-0 flex items-center justify-end"
                initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: -16 }}
                animate={
                  reduce
                    ? { opacity: 1, y: 0 }
                    : {
                        opacity: [0, 1, 1, 0],
                        y: [-16, 0, 0, 16],
                      }
                }
                transition={
                  reduce
                    ? undefined
                    : {
                        duration,
                        times: [0, 0.15, 0.85, 1],
                        repeat: Infinity,
                        delay: (i * duration) / credits.length,
                        ease: "easeInOut",
                      }
                }
              >
                {n}
              </motion.span>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            left
          </div>
        </div>
      </div>

      {/* Session log toast that pops in to "consume" a credit */}
      <motion.div
        className="mt-4 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs"
        initial={reduce ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
        animate={
          reduce
            ? { opacity: 1, x: 0 }
            : { opacity: [0, 1, 1, 0], x: [-20, 0, 0, 20] }
        }
        transition={
          reduce
            ? undefined
            : {
                duration: duration * credits.length,
                times: [0, 0.1, 0.4, 0.5],
                repeat: Infinity,
                ease: "easeInOut",
              }
        }
      >
        <ClipboardCheck className="h-3.5 w-3.5 text-brand-text" />
        <span className="font-medium">Lesson logged</span>
        <span className="text-muted-foreground">— 1 credit used</span>
      </motion.div>
    </div>
  );
}
